/**
 * Integration Health — channel connection operational semantics
 *
 * Derives the health state of an external integration (Amazon SP-API) from its
 * connection and sync lifecycle fields. A degraded or disconnected integration
 * means the ERP's product and order state is diverging from the source of truth.
 *
 * Priority order (most urgent first):
 *   1. disconnected   — status is 'disconnected' or 'inactive'
 *                       No data exchange is possible. Action required.
 *   2. auth-error     — status is 'expired' or code is an auth/token error
 *                       Token rotation failed. Re-authentication required.
 *   3. sync-error     — status is 'error' with a non-auth error code
 *                       API returned a failure. May self-resolve or need attention.
 *   4. stale          — status is 'active' but last sync exceeds the stale threshold
 *                       Connection is alive but data is going out of date.
 *   5. pending        — status is 'pending', never completed a sync
 *                       Newly connected; first sync in progress or not yet triggered.
 *   6. healthy        — active, synced within the stale threshold, no errors
 *
 * Staleness threshold:
 *   Uses syncPreferences.syncInterval (minutes) × STALE_MULTIPLIER.
 *   Falls back to DEFAULT_SYNC_INTERVAL_MINUTES when not set.
 *   A sync is stale when hoursElapsed(lastSyncedAt) > threshold hours.
 *
 * Temporal metrics (undefined when not applicable):
 *   hoursSinceSync     — hours since last successful sync (all levels with a sync date)
 *   hoursSinceError    — hours since the last recorded error (error levels)
 *   syncAgeLabel       — human-readable sync age (e.g. "3h ago", "2d ago")
 *   staleThresholdHours — the computed staleness threshold in hours
 *
 * The `icon` field is a string name resolved by the panel component via ICON_MAP.
 *
 * derive, never store — all time calculations use live Date.now()
 */

import { hoursElapsed, formatAge } from '../shared/time.js';

// ── Constants ─────────────────────────────────────────────────────────────────

/** Default sync interval when syncPreferences.syncInterval is absent */
const DEFAULT_SYNC_INTERVAL_MINUTES = 15;

/**
 * A sync is considered stale when elapsed time exceeds:
 *   syncInterval × STALE_MULTIPLIER hours
 * e.g. 15 min interval → stale after 15 × 4 = 60 min (1 hour)
 *      30 min interval → stale after 30 × 4 = 120 min (2 hours)
 */
const STALE_MULTIPLIER = 4;

/**
 * Error codes that indicate an authentication/token failure rather than an
 * operational API error. Used to distinguish auth-error from sync-error.
 */
const AUTH_ERROR_CODES = new Set([
  'UNAUTHORIZED',
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'REFRESH_TOKEN_EXPIRED',
  'ACCESS_DENIED',
  'INVALID_GRANT',
  'INVALID_CLIENT',
  'AUTH_FAILURE',
]);

// ── Sort order (lower index = more urgent) ────────────────────────────────────

export const HEALTH_SORT_ORDER = [
  'disconnected',
  'auth-error',
  'sync-error',
  'stale',
  'pending',
  'healthy',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function isAuthErrorCode(code) {
  if (!code) return false;
  return AUTH_ERROR_CODES.has(code.toUpperCase());
}

function computeStaleThresholdHours(syncIntervalMinutes) {
  const interval = (typeof syncIntervalMinutes === 'number' && syncIntervalMinutes > 0)
    ? syncIntervalMinutes
    : DEFAULT_SYNC_INTERVAL_MINUTES;
  return (interval * STALE_MULTIPLIER) / 60;
}

// ── Main classifier ───────────────────────────────────────────────────────────

/**
 * @param {object} credential — AmazonCredential (or similar) document from API
 * @param {string}  credential.status          — 'active'|'expired'|'error'|'disconnected'|'pending'|'inactive'
 * @param {Date|string} [credential.lastSyncedAt]
 * @param {{ message?: string, timestamp?: Date|string, code?: string }} [credential.lastError]
 * @param {{ syncInterval?: number }} [credential.syncPreferences]
 * @param {{ errors?: number, totalOrdersSynced?: number }} [credential.stats]
 *
 * @returns {{
 *   level: string,
 *   label: string,
 *   sub: string|null,
 *   icon: string,
 *   rowBg: string,
 *   rowClass: string,
 *   badgeClass: string,
 *   barColor: string,
 *   hoursSinceSync?: number,
 *   hoursSinceError?: number,
 *   syncAgeLabel?: string,
 *   staleThresholdHours: number,
 *   errorMessage?: string,
 *   errorCode?: string,
 * }}
 */
export function computeIntegrationHealth(credential) {
  const {
    status = 'disconnected',
    lastSyncedAt,
    lastError,
    syncPreferences,
  } = credential || {};

  const syncInterval = syncPreferences?.syncInterval;
  const staleThresholdHours = computeStaleThresholdHours(syncInterval);

  // Compute shared metrics
  const hoursSinceSync = lastSyncedAt ? hoursElapsed(lastSyncedAt) : undefined;
  const syncAgeLabel = hoursSinceSync !== undefined ? formatAge(hoursSinceSync) : undefined;
  const hoursSinceError = lastError?.timestamp ? hoursElapsed(lastError.timestamp) : undefined;

  const sharedMetrics = {
    staleThresholdHours,
    ...(hoursSinceSync !== undefined && { hoursSinceSync, syncAgeLabel }),
    ...(hoursSinceError !== undefined && { hoursSinceError }),
    ...(lastError?.message && { errorMessage: lastError.message }),
    ...(lastError?.code    && { errorCode:    lastError.code }),
  };

  // ── Level 1: disconnected ──────────────────────────────────────────────────
  if (status === 'disconnected' || status === 'inactive') {
    return {
      level:      'disconnected',
      label:      'Disconnected',
      sub:        null,
      icon:       'Unlink',
      rowBg:      'bg-red-50 dark:bg-red-900/10',
      rowClass:   'border-l-2 border-red-400',
      badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      barColor:   'bg-red-600',
      ...sharedMetrics,
    };
  }

  // ── Level 2: auth-error ────────────────────────────────────────────────────
  if (status === 'expired' || (status === 'error' && isAuthErrorCode(lastError?.code))) {
    return {
      level:      'auth-error',
      label:      'Auth Error',
      sub:        status === 'expired' ? 'Token expired' : 'Re-auth required',
      icon:       'KeyRound',
      rowBg:      'bg-red-50 dark:bg-red-900/10',
      rowClass:   'border-l-2 border-red-500',
      badgeClass: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      barColor:   'bg-red-500',
      ...sharedMetrics,
    };
  }

  // ── Level 3: sync-error ────────────────────────────────────────────────────
  if (status === 'error') {
    return {
      level:      'sync-error',
      label:      'Sync Error',
      sub:        lastError?.code ?? null,
      icon:       'AlertCircle',
      rowBg:      'bg-orange-50 dark:bg-orange-900/10',
      rowClass:   'border-l-2 border-orange-400',
      badgeClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      barColor:   'bg-orange-500',
      ...sharedMetrics,
    };
  }

  // ── Level 4: stale ─────────────────────────────────────────────────────────
  // Only applies to 'active' connections that haven't synced within threshold
  if (status === 'active' && hoursSinceSync !== undefined && hoursSinceSync > staleThresholdHours) {
    return {
      level:      'stale',
      label:      'Stale',
      sub:        syncAgeLabel,
      icon:       'Clock',
      rowBg:      'bg-amber-50 dark:bg-amber-900/10',
      rowClass:   'border-l-2 border-amber-400',
      badgeClass: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
      barColor:   'bg-amber-400',
      ...sharedMetrics,
    };
  }

  // ── Level 5: pending ───────────────────────────────────────────────────────
  // Newly connected; never completed a sync, OR explicitly pending status
  if (status === 'pending' || (status === 'active' && !lastSyncedAt)) {
    return {
      level:      'pending',
      label:      'Pending',
      sub:        'Awaiting first sync',
      icon:       'Loader',
      rowBg:      '',
      rowClass:   'border-l-2 border-blue-300',
      badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      barColor:   'bg-blue-400',
      ...sharedMetrics,
    };
  }

  // ── Level 6: healthy ───────────────────────────────────────────────────────
  return {
    level:      'healthy',
    label:      'Healthy',
    sub:        syncAgeLabel ?? null,
    icon:       'CheckCircle',
    rowBg:      '',
    rowClass:   '',
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    barColor:   'bg-green-500',
    ...sharedMetrics,
  };
}
